import { getStorage, ref, uploadString } from 'firebase/storage';
import firebaseApp from './firebase';
const storage = getStorage(firebaseApp);

/**
 * Calculate the size of a JavaScript object in bytes
 * @param {any} obj - The object to measure
 * @returns {number} Size in bytes
 */
export function calculateObjectSize(obj) {
  const jsonString = JSON.stringify(obj);
  const baseSize = new Blob([jsonString]).size;
  // Add 30% buffer for Firestore Protocol Buffer overhead
  return Math.ceil(baseSize * 1.3);
}
/**
 * Analyze an object to identify which properties are taking up the most space
 * @param {object} obj - The object to analyze
 * @param {number} maxDepth - Maximum depth to analyze (default: 2)
 * @returns {Array<{key: string, size: number, percentage: number}>} Array of property sizes
 */
export function analyzeObjectSize(obj, maxDepth = 2) {
  if (typeof obj !== 'object' || obj === null) {
    return [];
  }

  const totalSize = calculateObjectSize(obj);
  const breakdown = [];

  for (const [key, value] of Object.entries(obj)) {
    const size = calculateObjectSize(value);
    const percentage = ((size / totalSize) * 100).toFixed(2);

    const item = {
      key,
      size,
      sizeFormatted: formatBytes(size),
      percentage: parseFloat(percentage),
      type: Array.isArray(value) ? `Array(${value.length})` : typeof value
    };

    // If it's an object or array and we haven't reached max depth, analyze further
    if (maxDepth > 0 && typeof value === 'object' && value !== null) {
      if (Array.isArray(value) && value.length > 0) {
        // For arrays, show sample item size
        item.sampleItemSize = formatBytes(calculateObjectSize(value[0]));
      } else if (!Array.isArray(value)) {
        // For objects, recursively analyze
        item.children = analyzeObjectSize(value, maxDepth - 1);
      }
    }

    breakdown.push(item);
  }

  return breakdown.sort((a, b) => b.size - a.size);
}

/**
 * Format bytes to human-readable format
 * @param {number} bytes - Number of bytes
 * @returns {string} Formatted string (e.g., "1.5 MB")
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Get Firebase Console URL for a storage file
 * @param {string} storagePath - Path in Firebase Storage
 * @returns {string} Firebase Console URL
 */
function getStorageConsoleUrl(storagePath) {
  const projectId = firebaseApp.options.projectId;
  const bucket = firebaseApp.options.storageBucket;
  const encodedPath = encodeURIComponent(storagePath);
  return `https://console.firebase.google.com/project/${projectId}/storage/${bucket}/files/~2F${encodedPath.replace(/%2F/g, '~2F')}`;
}

/**
 * Upload oversized document JSON to Firebase Storage (auth-protected)
 * @param {string} collection - Collection name
 * @param {string} documentId - Document ID (or 'pending')
 * @param {object} data - Document data
 * @param {number} size - Document size in bytes
 * @returns {Promise<{storagePath: string, consoleUrl: string}>}
 */
export async function uploadOversizedDocument(collection, documentId, data, size) {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
  const oversizedDocumentFilename = `${collection}_${documentId}_${timestamp}.json`;
  const storagePath = `oversized-documents/${oversizedDocumentFilename}`;
  try {
    const storageRef = ref(storage, storagePath);
    await uploadString(storageRef, JSON.stringify(data, null, 2), 'raw', {
      contentType: 'application/json'
    });

    const consoleUrl = getStorageConsoleUrl(storagePath);

    console.warn('='.repeat(80));
    console.warn('OVERSIZED DOCUMENT STORED TO FIREBASE STORAGE');
    console.warn('='.repeat(80));
    console.warn(`Collection: ${collection}`);
    console.warn(`Document ID: ${documentId}`);
    console.warn(`Total Size: ${formatBytes(size)}`);
    console.warn(`Stored at: ${consoleUrl}`);
    console.warn('='.repeat(80));

    return { storagePath, consoleUrl };
  } catch (error) {
    console.error('Error uploading oversized document:', error);
    throw error;
  }
}
/**
 * If document exceeds Firestore size limit → upload to Storage instead,
 * and return lightweight object containing the download URL.
 * Otherwise, return original data.
 */
export async function checkAndHandleDocumentSize(collection, documentId, data) {
  const size = calculateObjectSize(data);
  const FIRESTORE_LIMIT = 1048576; // 1 MB

  if (size > FIRESTORE_LIMIT) {
    const { consoleUrl, storagePath } = await uploadOversizedDocument(
      collection,
      documentId,
      data,
      size
    );

    return {
      oversized: true,
      consoleUrl,
      storagePath,
      originalSize: size
    };
  }

  return null;
}

/**
 * Create a summary report of what's making a document large
 * @param {object} data - Document data
 * @returns {string} Human-readable summary
 */
export function createSizeSummary(data) {
  const size = calculateObjectSize(data);
  const breakdown = analyzeObjectSize(data, 2);

  let summary = `Total Document Size: ${formatBytes(size)}\n\n`;
  summary += 'Top 10 Largest Properties:\n';
  summary += '='.repeat(80) + '\n';

  breakdown.slice(0, 10).forEach((item, index) => {
    summary += `${index + 1}. ${item.key}\n`;
    summary += `   Size: ${item.sizeFormatted} (${item.percentage}%)\n`;
    summary += `   Type: ${item.type}\n`;

    if (item.sampleItemSize) {
      summary += `   Sample Item Size: ${item.sampleItemSize}\n`;
    }

    if (item.children && item.children.length > 0) {
      summary += '   Nested Properties:\n';
      item.children.slice(0, 5).forEach((child) => {
        summary += `     - ${child.key}: ${child.sizeFormatted} (${child.percentage}% of parent)\n`;
      });
    }
    summary += '\n';
  });

  return summary;
}
